/**
 * Codec ordering for `RTCRtpTransceiver.setCodecPreferences` from the tenant policy
 * (`policy.codecs`, default G722, PCMA, PCMU, opus). Preferred codecs come first in policy
 * order; everything else (telephone-event for RFC 4733 DTMF, CN, red...) keeps its original
 * relative order after them so DTMF never disappears from the offer.
 */
export interface CodecLike {
  mimeType: string;
  clockRate?: number;
  channels?: number;
  sdpFmtpLine?: string;
}

function codecName(mimeType: string): string {
  const i = mimeType.indexOf('/');
  return (i >= 0 ? mimeType.slice(i + 1) : mimeType).toLowerCase();
}

export function orderCodecs<T extends CodecLike>(capabilities: readonly T[], preferred: readonly string[]): T[] {
  const wanted = preferred.map((p) => p.toLowerCase());
  const head: T[] = [];
  for (const name of wanted) {
    for (const c of capabilities) if (codecName(c.mimeType) === name && !head.includes(c)) head.push(c);
  }
  const tail = capabilities.filter((c) => !head.includes(c));
  return [...head, ...tail];
}

export type CapabilitiesFn = (kind: string) => { codecs: RTCRtpCodec[] } | null;

/** applies the ordering to every audio transceiver; returns how many transceivers were touched */
export function applyCodecPreferences(
  pc: Pick<RTCPeerConnection, 'getTransceivers'>,
  preferred: readonly string[],
  getCapabilities: CapabilitiesFn = (kind) => (typeof RTCRtpReceiver !== 'undefined' && 'getCapabilities' in RTCRtpReceiver ? RTCRtpReceiver.getCapabilities(kind) : null),
): number {
  const caps = getCapabilities('audio');
  if (!caps || caps.codecs.length === 0) return 0;
  const ordered = orderCodecs(caps.codecs, preferred);
  let n = 0;
  for (const t of pc.getTransceivers()) {
    const kind = t.receiver?.track?.kind ?? t.sender?.track?.kind;
    if (kind !== 'audio' && kind !== undefined) continue;
    if (typeof t.setCodecPreferences !== 'function') continue;
    try {
      t.setCodecPreferences(ordered);
      n += 1;
    } catch {
      // an unsupported combination must never break the call setup
    }
  }
  return n;
}
